import React from "react";
import { useNavigate } from "react-router-dom";
import { emailSignIn, googleSignIn } from "../firebase/auth";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  MdRadar,
  MdEmail,
  MdLock,
  MdVisibility,
  MdVisibilityOff,
} from "react-icons/md";
import { FcGoogle } from "react-icons/fc";

export default function Login(): React.ReactElement {
  const nav = useNavigate();
  const [email, setEmail] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");
  const [showPwd, setShowPwd] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  async function onEmailLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await emailSignIn(email.trim(), password);
      nav("/", { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function onGoogle() {
    setError(null);
    setLoading(true);
    try {
      await googleSignIn();
      nav("/", { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen bg-app">
      <div
        className="pointer-events-none absolute inset-0 opacity-50"
        style={{
          background:
            "radial-gradient(1200px 600px at 10% -10%, var(--color-primary) 0%, transparent 60%), radial-gradient(1000px 600px at 110% 110%, var(--color-secondary) 0%, transparent 60%)",
        }}
      />

      <div className="relative mx-auto grid min-h-screen max-w-6xl grid-cols-1 items-center gap-8 px-6 py-10 md:grid-cols-2">
        <div className="order-2 hidden md:order-1 md:block">
          <div className="rounded-2xl border border-surface bg-surface p-8 shadow-soft">
            <div className="mb-6 flex items-center gap-3">
              <div
                className="grid h-12 w-12 place-items-center rounded-2xl"
                style={{
                  backgroundColor:
                    "color-mix(in oklab, var(--color-primary) 12%, transparent)",
                }}
              >
                <MdRadar
                  className="text-2xl"
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <div>
                <h1 className="text-xl font-bold">Radar4 Dashboard</h1>
                <p className="text-sm text-on-surface/70">
                  Administración y contenidos
                </p>
              </div>
            </div>

            <ul className="space-y-3 text-sm text-on-surface/80">
              <li>• Edición de usuarios y roles</li>
              <li>• Gestión de cursos y comunidad</li>
              <li>• Cuestionarios y certificaciones</li>
            </ul>
          </div>
        </div>

        <div className="order-1 md:order-2">
          <form
            onSubmit={onEmailLogin}
            className="mx-auto w-full max-w-md rounded-2xl border border-surface bg-surface p-6 shadow-xl"
          >
            <div className="mb-5 flex items-center gap-3">
              <div
                className="grid h-10 w-10 place-items-center rounded-xl"
                style={{
                  backgroundColor:
                    "color-mix(in oklab, var(--color-primary) 12%, transparent)",
                }}
              >
                <MdRadar
                  className="text-xl"
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Iniciar sesión</h2>
                <p className="text-xs text-on-surface/70">
                  Usa tu cuenta autorizada
                </p>
              </div>
            </div>

            <label className="mb-1 block text-xs font-medium text-on-surface/80">
              Email
            </label>
            <div className="mb-3 relative">
              <MdEmail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-on-surface/60" />
              <input
                type="email"
                className="w-full rounded-lg border border-surface bg-surface py-2 pl-10 pr-3 outline-none focus:ring-2"
                style={{ boxShadow: "inset 0 0 0 9999px transparent" }}
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                required
                autoComplete="email"
                inputMode="email"
              />
            </div>

            <label className="mb-1 block text-xs font-medium text-on-surface/80">
              Contraseña
            </label>
            <div className="mb-2 relative">
              <MdLock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-on-surface/60" />
              <input
                type={showPwd ? "text" : "password"}
                className="w-full rounded-lg border border-surface bg-surface py-2 pl-10 pr-10 outline-none focus:ring-2"
                style={{ boxShadow: "inset 0 0 0 9999px transparent" }}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                aria-label={
                  showPwd ? "Ocultar contraseña" : "Mostrar contraseña"
                }
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-on-surface/70 hover:bg-black/5 dark:hover:bg-white/10"
                onClick={() => setShowPwd((v) => !v)}
                disabled={loading}
              >
                {showPwd ? <MdVisibilityOff /> : <MdVisibility />}
              </button>
            </div>

            {error && (
              <p
                className="mb-3 text-sm text-red-600"
                role="alert"
                aria-live="polite"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="group relative mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-white disabled:opacity-60"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {loading && (
                <span className="absolute left-3 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/50 border-t-transparent" />
              )}
              Entrar con email
            </button>

            <div className="my-4 flex items-center gap-3 text-xs text-on-surface/60">
              <div
                className="h-px flex-1"
                style={{
                  background:
                    "color-mix(in oklab, var(--color-onSurface) 12%, transparent)",
                }}
              />
              ó
              <div
                className="h-px flex-1"
                style={{
                  background:
                    "color-mix(in oklab, var(--color-onSurface) 12%, transparent)",
                }}
              />
            </div>

            <button
              type="button"
              onClick={onGoogle}
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-surface bg-surface px-4 py-2 hover:bg-primary/10 disabled:opacity-60"
            >
              <FcGoogle className="text-xl" />
              Continuar con Google
            </button>

            {loading && (
              <div className="pt-3">
                <LoadingSpinner label="Iniciando sesión…" />
              </div>
            )}

            {/* Footer legal + crédito */}
            <p className="mt-4 text-center text-xs text-on-surface/60">
              © {new Date().getFullYear()} Radar4 — acceso restringido
            </p>
            <p className="mt-1 text-center text-[11px] text-on-surface/50">
              Powered by{" "}
              <a
                href="https://alexgeekresume.web.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-dotted hover:decoration-solid hover:text-primary"
              >
                Alex Welsh
              </a>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
